/**
 * Adapter para la API de Tracking
 * Adapter for Tracking API
 */

import { HttpClient } from '@adapters/HttpClient';
import {
  FilesComparisonResponse,
  WebhooksComparisonResponse,
  ApiComparisonResponse,
  CourierStatusResponse,
  OutdatedOrdersResponse,
  HistorySummaryResponse,
  HistoryFailedResponse,
  RetryHistoryRequest,
  RetryHistoryResponse,
  UnmatchedRecordsResponse,
} from '../models/TrackingApiResponse';
import { TrackingFilters, PaginationParams } from '../models/TrackingFilters';

export class TrackingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene comparativa de archivos procesados
   * Gets files comparison data
   */
  async getFilesComparison(
    filters?: TrackingFilters
  ): Promise<FilesComparisonResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.hours !== undefined) {
      params.hours = filters.hours;
    }

    return this.httpClient.get<FilesComparisonResponse>(
      '/tracking/dashboard/files/comparison',
      params
    );
  }

  /**
   * Obtiene comparativa de webhooks recibidos
   * Gets webhooks comparison data
   */
  async getWebhooksComparison(
    filters?: TrackingFilters
  ): Promise<WebhooksComparisonResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.hours !== undefined) {
      params.hours = filters.hours;
    }

    return this.httpClient.get<WebhooksComparisonResponse>(
      '/tracking/dashboard/webhooks/comparison',
      params
    );
  }

  /**
   * Obtiene comparativa de API calls
   * Gets API calls comparison data
   */
  async getApiComparison(
    filters?: TrackingFilters
  ): Promise<ApiComparisonResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.hours !== undefined) {
      params.hours = filters.hours;
    }

    return this.httpClient.get<ApiComparisonResponse>(
      '/tracking/dashboard/api/comparison',
      params
    );
  }

  /**
   * Obtiene el estado de las APIs por courier
   * Gets API status by courier
   */
  async getCourierStatus(): Promise<CourierStatusResponse> {
    return this.httpClient.get<CourierStatusResponse>(
      '/tracking/api-calls/couriers/status'
    );
  }

  /**
   * Obtiene pedidos desfasados
   * Gets outdated orders
   */
  async getOutdatedOrders(
    filters?: TrackingFilters,
    pagination?: PaginationParams
  ): Promise<OutdatedOrdersResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.minutes_threshold !== undefined) {
      params.minutes_threshold = filters.minutes_threshold;
    }
    if (pagination?.page !== undefined) {
      params.page = pagination.page;
    }
    if (pagination?.per_page !== undefined) {
      params.per_page = pagination.per_page;
    }

    return this.httpClient.get<OutdatedOrdersResponse>(
      '/tracking/api-calls/outdated-orders',
      params
    );
  }

  /**
   * Obtiene resumen de sincronización de histórico
   * Gets history sync summary
   */
  async getHistorySummary(
    filters?: TrackingFilters
  ): Promise<HistorySummaryResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    // La API usa days_ago como parámetro, no date_from
    // Por defecto usa 7 días si no se especifica

    return this.httpClient.get<HistorySummaryResponse>(
      '/tracking/history/summary',
      params
    );
  }

  /**
   * Obtiene registros fallidos de sincronización de histórico
   * Gets failed history sync records
   */
  async getHistoryFailed(
    filters?: TrackingFilters,
    pagination?: PaginationParams
  ): Promise<HistoryFailedResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.retry_count !== undefined) {
      params.retry_count = filters.retry_count;
    }
    if (filters?.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters?.date_to) {
      params.date_to = filters.date_to;
    }
    if (pagination?.page !== undefined) {
      params.page = pagination.page;
    }
    if (pagination?.per_page !== undefined) {
      params.per_page = pagination.per_page;
    }

    return this.httpClient.get<HistoryFailedResponse>(
      '/tracking/history/failed',
      params
    );
  }

  /**
   * Reintenta sincronización de histórico
   * Retries history sync
   */
  async retryHistory(
    request: RetryHistoryRequest
  ): Promise<RetryHistoryResponse> {
    return this.httpClient.post<RetryHistoryResponse>(
      '/tracking/history/retry',
      request
    );
  }

  /**
   * Obtiene registros no encontrados
   * Gets unmatched records
   */
  async getUnmatchedRecords(
    filters?: TrackingFilters,
    pagination?: PaginationParams
  ): Promise<UnmatchedRecordsResponse> {
    const params: Record<string, any> = {};

    if (filters?.courier_id !== undefined && filters.courier_id !== null) {
      params.courier_id = filters.courier_id;
    }
    if (filters?.source) {
      params.source = filters.source;
    }
    if (filters?.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters?.date_to) {
      params.date_to = filters.date_to;
    }
    if (pagination?.page !== undefined) {
      params.page = pagination.page;
    }
    if (pagination?.per_page !== undefined) {
      params.per_page = pagination.per_page;
    }

    return this.httpClient.get<UnmatchedRecordsResponse>(
      '/tracking/unmatched',
      params
    );
  }
}

